import { Avatar, type AvatarProps } from '@magicschool/spellbook/components/Avatar';
import { Button } from '@magicschool/spellbook/components/Button';
import { useDropdownMenu } from '@magicschool/spellbook/components/DropdownMenu/DropdownMenuProvider';
import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { ControlledModalTrigger, Modal, type ModalProps } from '@magicschool/spellbook/components/Modal';
import { specifyModalReturnFocus, trackLastClickedTrigger } from '@magicschool/spellbook/components/Modal/modalFocusCoordination';
import {
  ModalCloseSlot,
  ModalContentSlot,
  ModalDescriptionSlot,
  ModalFooterSlot,
  ModalHeaderSlot,
} from '@magicschool/spellbook/components/Modal/slots';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useRenderedChildren } from '@magicschool/spellbook/hooks/useRenderedChildren';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { popperContentMotionClassNames, popperContentSideOffsetClassNames } from '@magicschool/spellbook/lib/popperContent';
import type { SpellbookNameOverrideProps } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { useDirection } from 'radix-ui/direction';
import * as DropdownMenuPrimitive from 'radix-ui/dropdown-menu';
import type { ComponentProps, MouseEvent, ReactNode, RefObject } from 'react';
import { Children, cloneElement, forwardRef, isValidElement, memo, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

export type DropdownMenuItemProps = ComponentProps<typeof DropdownMenuPrimitive.Item> & SpellbookNameOverrideProps;

export const DropdownMenuTriggerSlot = Slot<ComponentProps<typeof DropdownMenuPrimitive.Trigger>>({
  displayName: 'DropdownMenu.Trigger',
  preventOverride: true,
  renderDefault: (props) => {
    const DropdownMenuTrigger = useMemo(
      () =>
        forwardRef<HTMLButtonElement, ComponentProps<typeof DropdownMenuPrimitive.Trigger>>(
          ({ children, asChild = true, ...restProps }, forwardedRef) => {
            const { triggerRef } = useDropdownMenu();

            // Combine forwarded ref with internal context ref
            useImperativeHandle(forwardedRef, () => triggerRef.current as HTMLButtonElement);

            return (
              <DropdownMenuPrimitive.Trigger data-spellbook-name="dropdown-menu-trigger" asChild={asChild} ref={triggerRef} {...restProps}>
                {typeof children === 'string' && children.length > 0 ? <button type="button">{children}</button> : children}
              </DropdownMenuPrimitive.Trigger>
            );
          },
        ),
      [],
    );

    return <DropdownMenuTrigger {...props} />;
  },
});

export const DropdownMenuTooltipSlot = Slot<
  Omit<TooltipProps, 'asToggleTip'> & {
    'data-testid'?: string;
  }
>({
  displayName: 'DropdownMenu.Tooltip',
  spellbookName: false,
  preventOverride: true,
  renderDefault: ({ className, children, side = 'top', align = 'center', 'data-testid': dataTestId, ...props }) => {
    const { slots } = useSlots(children, {
      tooltipContent: { Component: DropdownMenuTooltipContentSlot },
      trigger: { Component: DropdownMenuTriggerSlot, props: { 'data-testid': dataTestId } },
    });

    return (
      <Tooltip className={cn('group', className)} side={side} align={align} {...props}>
        <Tooltip.Trigger>{slots.trigger}</Tooltip.Trigger>
        {slots.tooltipContent && <Tooltip.Content>{slots.tooltipContent}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const DropdownMenuTooltipContentSlot = Slot<TextProps>({
  displayName: 'DropdownMenu.TooltipContent',
  preventOverride: true,
  renderDefault: ({ ...props }) => (
    <Text data-spellbook-name="dropdown-menu-tooltip-content" variant="caption-1" element="span" {...props} />
  ),
});

export type DropdownMenuContentProps = ComponentProps<typeof DropdownMenuPrimitive.Content> & {
  portalContainer?: ComponentProps<typeof DropdownMenuPrimitive.Portal>['container'];
};

export const DropdownMenuContentSlot = Slot<DropdownMenuContentProps>({
  displayName: 'DropdownMenu.Content',
  isRenderedByDefault: true,
  preventOverride: true,
  renderDefault: ({ children, className, align = 'start', onCloseAutoFocus, portalContainer, ...props }) => {
    const { hasOpenModal } = useDropdownMenu();

    const handleOnCloseAutoFocus = (event: Event) => {
      onCloseAutoFocus?.(event);
    };

    if (!children) {
      return null;
    }

    return (
      <DropdownMenuPrimitive.Portal container={portalContainer}>
        <DropdownMenuPrimitive.Content
          data-spellbook-name="dropdown-menu-content"
          className={cn(
            ...popperContentSideOffsetClassNames,
            'bg-surface-overlay-0',
            ...popperContentMotionClassNames,
            'relative',
            /**
             * @TODO [SPE-74] Semantic z-indices
             * https://linear.app/magicschool/issue/SPE-74/semantic-z-indices
             */
            'z-3000',
            'border',
            'border-border-default',
            'min-w-42',
            'max-w-(--radix-dropdown-menu-content-available-width)',
            'max-h-(--radix-dropdown-menu-content-available-height)',
            'origin-(--radix-dropdown-menu-content-transform-origin)',
            'overflow-x-hidden',
            'border',
            'border-border-default',
            'overflow-y-auto',
            'rounded-card-md',
            'shadow-popover',
            'group',
            'focus-visible:outline-none',
            className,
          )}
          align={align}
          onCloseAutoFocus={handleOnCloseAutoFocus}
          hidden={hasOpenModal}
          {...props}
        >
          {children}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    );
  },
});

export const DropdownMenuHeaderSlot = Slot<ComponentProps<typeof DropdownMenuPrimitive.Label>>({
  displayName: 'DropdownMenu.Header',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <DropdownMenuPrimitive.Label data-spellbook-name="dropdown-menu-header" {...props} asChild>
        <Text variant="caption-1" color="default-soft" className={cn('px-3 pt-4 flex', className)}>
          {children}
        </Text>
      </DropdownMenuPrimitive.Label>
    );
  },
});

export const DropdownMenuFooterSlot = Slot<ComponentProps<typeof DropdownMenuPrimitive.Label>>({
  displayName: 'DropdownMenu.Footer',
  renderDefault: ({ children, className, ...props }) => {
    return (
      <DropdownMenuPrimitive.Label data-spellbook-name="dropdown-menu-footer" {...props} asChild>
        <Text variant="caption-1" color="default-soft" className={cn('px-3 pb-4 flex', className)}>
          {children}
        </Text>
      </DropdownMenuPrimitive.Label>
    );
  },
});

export const DropdownMenuGroupSlot = Slot<ComponentProps<typeof DropdownMenuPrimitive.Group>>({
  displayName: 'DropdownMenu.Group',
  renderDefault: ({ children, className, 'aria-labelledby': ariaLabelledBy, ...props }) => {
    const generatedHeaderId = useId();
    let headerId: string | undefined;
    const labelledChildren = Children.map(children, (child) => {
      if (headerId || !isValidElement<{ id?: string }>(child) || child.type !== DropdownMenuHeaderSlot) return child;
      headerId = child.props.id ?? generatedHeaderId;
      return cloneElement(child, { id: headerId });
    });

    return (
      <DropdownMenuPrimitive.Group
        data-spellbook-name="dropdown-menu-group"
        className={cn('border-t border-t-border-default first:border-0', className)}
        aria-labelledby={ariaLabelledBy ?? headerId}
        {...props}
      >
        {labelledChildren}
      </DropdownMenuPrimitive.Group>
    );
  },
});

type DropdownMenuItemSlottedContentProps = {
  slots: {
    avatar?: ReactNode;
    leadingIcon?: ReactNode;
    trailingIcon?: ReactNode;
    label?: ReactNode;
    description?: ReactNode;
  };
};

const dropdownMenuRowClassName = ['flex', 'items-center', 'gap-2', 'px-3', 'py-2', 'text-default-text-strong', 'first:mt-2', 'last:mb-2'];
const dropdownMenuRowTextClassName = 'flex grow-1 flex-col me-4';

const DropdownMenuItemSlottedContent = memo<DropdownMenuItemSlottedContentProps>(({ slots }) => (
  <span className="flex-1 min-w-0">
    <span className="w-full flex items-center gap-2">
      {slots.avatar}
      {slots.leadingIcon}
      <span className={cn(dropdownMenuRowTextClassName, 'group-aria-disabled:cursor-text')}>
        {slots.label}
        {slots.description}
      </span>
      {slots.trailingIcon && <span className="flex-0">{slots.trailingIcon}</span>}
    </span>
  </span>
));

const DropdownMenuItemBase = Slot<DropdownMenuItemProps>({
  displayName: 'DropdownMenu.Item',
  renderDefault: ({ asChild, className, disabled, children, onClick, 'data-spellbook-name': spellbookName, ...props }) => {
    const { slots, rest } = useSlots(children, {
      avatar: { Component: DropdownMenuItemAvatarSlot, props },
      leadingIcon: { Component: DropdownMenuItemLeadingIconSlot, props },
      trailingIcon: { Component: DropdownMenuItemTrailingIconSlot, props },
      label: { Component: DropdownMenuItemLabelSlot, props },
      description: { Component: DropdownMenuItemDescriptionSlot, props },
    });

    const { renderedChildren } = useRenderedChildren(children, {
      slots: {
        avatar: { Component: DropdownMenuItemAvatarSlot, props },
        leadingIcon: { Component: DropdownMenuItemLeadingIconSlot, props },
        trailingIcon: { Component: DropdownMenuItemTrailingIconSlot, props },
        label: { Component: DropdownMenuItemLabelSlot, props },
        description: { Component: DropdownMenuItemDescriptionSlot, props },
      },
      renderContent: ({ slots }) => <DropdownMenuItemSlottedContent slots={slots} />,
    });

    if (!children) {
      return null;
    }

    const handleOnClick = (event: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      onClick?.(event);
    };

    /**
     * @TODO [SPE-37] Utility for post-slot prop updates
     * https://linear.app/magicschool/issue/SPE-37/utility-for-post-slot-prop-updates
     */
    if (rest) {
      const labelSlot = slots.label;
      let labelSlotProps;
      if (Array.isArray(labelSlot) && labelSlot[0]) {
        labelSlotProps = {
          ...(labelSlot[0].props ?? {}),
          label: (
            <>
              {labelSlot[0].props.label}
              {rest}
            </>
          ),
        };
        slots.label = [
          {
            ...labelSlot[0],
            type: labelSlot[0].type,
            props: labelSlotProps,
          },
          ...labelSlot.slice(1),
        ];
      } else if (labelSlot && !Array.isArray(labelSlot)) {
        labelSlotProps = {
          ...(labelSlot.props ?? {}),
          label: (
            <>
              {labelSlot.props.label}
              {rest}
            </>
          ),
        };
        slots.label = {
          ...labelSlot,
          props: labelSlotProps,
        };
      }
    }

    return (
      <DropdownMenuPrimitive.Item
        data-spellbook-name="dropdown-menu-item"
        className={cn(
          dropdownMenuRowClassName,
          [
            'cursor-pointer',
            'justify-between',
            'outline-none',
            'hover:bg-surface-overlay-1',
            'focus-visible:bg-surface-overlay-1',
            'group',
            'aria-disabled:cursor-default',
            'aria-disabled:hover:bg-surface-overlay-0',
            'data-[disabled]:text-disabled-foreground',
          ],
          className,
        )}
        aria-disabled={disabled}
        asChild={asChild}
        onClick={handleOnClick}
        {...props}
        {...(spellbookName && { 'data-spellbook-name': spellbookName })}
      >
        {asChild ? renderedChildren : <DropdownMenuItemSlottedContent slots={slots} />}
      </DropdownMenuPrimitive.Item>
    );
  },
});

export const DropdownMenuItemContentOnlySlot = Slot<ComponentProps<'div'>>({
  displayName: 'DropdownMenu.ItemContentOnly',
  renderDefault: (props) => <div data-spellbook-name="dropdown-menu-item-content-only" {...props} />,
});

export type DropdownMenuActionRowActionProps = DropdownMenuItemProps & {
  /** Stretches the button across the row. Defaults to true when the row has no label or description. */
  fullWidth?: boolean;
};

export const DropdownMenuActionRowActionSlot = Slot<DropdownMenuActionRowActionProps>({
  displayName: 'DropdownMenu.ActionRowAction',
  preventOverride: true,
  renderDefault: ({ asChild, children, className, fullWidth, 'data-spellbook-name': spellbookName, ...props }) => (
    <DropdownMenuPrimitive.Item asChild {...props}>
      <Button
        data-spellbook-name="dropdown-menu-action-row-action"
        asChild={asChild}
        variant="outlined"
        size="sm"
        fullWidth={fullWidth}
        className={className}
        {...(spellbookName && { 'data-spellbook-name': spellbookName })}
      >
        {children}
      </Button>
    </DropdownMenuPrimitive.Item>
  ),
});

export type DropdownMenuActionRowProps = {
  children?: ReactNode;
  className?: string;
};

// Anything injected beyond children/className (ModalItem, Modal.Trigger) is handed to the Action, the only interactive element.
export const DropdownMenuActionRowSlot = Slot<DropdownMenuActionRowProps>({
  displayName: 'DropdownMenu.ActionRow',
  preventOverride: true,
  renderDefault: ({ children, className, ...injected }) => {
    const descriptionId = useId();
    const { slots, rest } = useSlots(children, {
      avatar: { Component: DropdownMenuItemAvatarSlot },
      leadingIcon: { Component: DropdownMenuItemLeadingIconSlot },
      trailingIcon: { Component: DropdownMenuItemTrailingIconSlot },
      label: { Component: DropdownMenuItemLabelSlot },
      description: { Component: DropdownMenuItemDescriptionSlot },
      action: { Component: DropdownMenuActionRowActionSlot },
    });
    const hasOwnLabel = isValidElement<{ children?: ReactNode }>(slots.label) && slots.label.props.children != null;
    const hasText = hasOwnLabel || Boolean(slots.description) || rest.length > 0;
    const action = isValidElement<DropdownMenuActionRowActionProps>(slots.action)
      ? cloneElement(slots.action, {
          fullWidth: slots.action.props.fullWidth ?? !hasText,
          ...(hasText && { 'aria-describedby': descriptionId }),
          ...injected,
        })
      : slots.action;

    return (
      <div data-spellbook-name="dropdown-menu-action-row" className={cn(dropdownMenuRowClassName, className)}>
        {slots.avatar}
        {slots.leadingIcon}
        {hasText && (
          <span id={descriptionId} className={dropdownMenuRowTextClassName}>
            {slots.label}
            {rest}
            {slots.description}
          </span>
        )}
        {slots.trailingIcon && <span className="flex-0">{slots.trailingIcon}</span>}
        <span className={hasText ? 'flex-0' : 'flex-1 min-w-0'}>{action}</span>
      </div>
    );
  },
});

export const DropdownMenuItemLeadingIconSlot = Slot<IconProps>({
  displayName: 'DropdownMenu.ItemLeadingIcon',
  renderDefault: ({ name = 'square-dashed', color = 'default', size = 'sm', ...props }) => (
    <Icon data-spellbook-name="dropdown-menu-item-leading-icon" name={name} color={color} size={size} {...props} />
  ),
});

export const DropdownMenuItemTrailingIconSlot = Slot<IconProps>({
  displayName: 'DropdownMenu.ItemTrailingIcon',
  renderDefault: ({ name = 'square-dashed', color = 'default', size = 'sm', ...props }) => (
    <Icon data-spellbook-name="dropdown-menu-item-trailing-icon" name={name} color={color} size={size} {...props} />
  ),
});

export const DropdownMenuItemLabelSlot = Slot<TextProps & { label?: ReactNode }>({
  displayName: 'DropdownMenu.ItemLabel',
  isRenderedByDefault: true,
  renderDefault: ({ children, variant = 'body-2', element = 'span', label, color = 'inherit', className, ...props }) => {
    return (
      <Text
        data-spellbook-name="dropdown-menu-item-label"
        variant={variant}
        element={element}
        color={color}
        className={cn('max-w-full shrink-0', className)}
        {...props}
      >
        {label}
        {children}
      </Text>
    );
  },
});

export const DropdownMenuItemDescriptionSlot = Slot<TextProps & { label?: ReactNode }>({
  displayName: 'DropdownMenu.ItemDescription',
  renderDefault: ({ children, className, variant = 'caption-1', element = 'span', label, color = 'default-soft', ...props }) => {
    return (
      <Text
        data-spellbook-name="dropdown-menu-item-description"
        variant={variant}
        element={element}
        color={color}
        className={cn('max-w-full shrink-0', className)}
        {...props}
      >
        {label}
        {children}
      </Text>
    );
  },
});

export const DropdownMenuItemAvatarSlot = Slot<AvatarProps>({
  displayName: 'DropdownMenu.ItemAvatar',
  renderDefault: ({ size = 'md', className, ...props }) => (
    <Avatar data-spellbook-name="dropdown-menu-item-avatar" size={size} className={cn('mr-1', className)} {...props} />
  ),
});

// Radix-native submenu components
export type DropdownMenuSubProps = ComponentProps<typeof DropdownMenuPrimitive.Sub>;

export const DropdownMenuSubSlot = Slot<DropdownMenuSubProps>({
  displayName: 'DropdownMenu.Sub',
  renderDefault: (props) => <DropdownMenuPrimitive.Sub data-spellbook-name="dropdown-menu-sub" {...props} />,
});

export type DropdownMenuSubTriggerProps = ComponentProps<typeof DropdownMenuPrimitive.SubTrigger>;

export const DropdownMenuSubTriggerSlot = Slot<DropdownMenuSubTriggerProps>({
  displayName: 'DropdownMenu.SubTrigger',
  renderDefault: ({ children, className, disabled, ...props }) => {
    const dir = useDirection();
    const isRtl = dir === 'rtl';

    const { slots, rest } = useSlots(children, {
      avatar: { Component: DropdownMenuItemAvatarSlot, props },
      leadingIcon: { Component: DropdownMenuItemLeadingIconSlot, props },
      trailingIcon: { Component: DropdownMenuItemTrailingIconSlot, props },
      label: { Component: DropdownMenuItemLabelSlot, props },
      description: { Component: DropdownMenuItemDescriptionSlot, props },
    });

    if (!children) {
      return null;
    }

    // Auto-add chevron icon
    const chevronIcon = <Icon name="chevron-right" color="default" size="sm" className={cn('ml-auto size-4', isRtl && 'rotate-180')} />;

    /**
     * @TODO [SPE-37] Utility for post-slot prop updates
     * https://linear.app/magicschool/issue/SPE-37/utility-for-post-slot-prop-updates
     */
    if (rest) {
      const labelSlot = slots.label;
      let labelSlotProps;
      if (Array.isArray(labelSlot) && labelSlot[0]) {
        labelSlotProps = {
          ...(labelSlot[0].props ?? {}),
          label: (
            <>
              {labelSlot[0].props.label}
              {rest}
            </>
          ),
        };
        slots.label = [
          {
            ...labelSlot[0],
            type: labelSlot[0].type,
            props: labelSlotProps,
          },
          ...labelSlot.slice(1),
        ];
      } else if (labelSlot && !Array.isArray(labelSlot)) {
        labelSlotProps = {
          ...(labelSlot.props ?? {}),
          label: (
            <>
              {labelSlot.props.label}
              {rest}
            </>
          ),
        };
        slots.label = {
          ...labelSlot,
          props: labelSlotProps,
        };
      }
    }

    return (
      <DropdownMenuPrimitive.SubTrigger
        data-spellbook-name="dropdown-menu-sub-trigger"
        className={cn(
          [
            'flex',
            'cursor-pointer',
            'items-center',
            'justify-between',
            'outline-none',
            'px-3',
            'py-2',
            'gap-2',
            'hover:bg-surface-overlay-1',
            'focus-visible:bg-surface-overlay-1',
            'data-[state=open]:bg-surface-overlay-1',
            'text-default-text-strong',
            'first:mt-2',
            'last:mb-2',
            'group',
            'aria-disabled:cursor-default',
            'aria-disabled:hover:bg-surface-overlay-0',
            'data-[disabled]:text-disabled-foreground',
          ],
          className,
        )}
        aria-disabled={disabled}
        {...props}
      >
        <DropdownMenuItemSlottedContent slots={slots} />
        {chevronIcon}
      </DropdownMenuPrimitive.SubTrigger>
    );
  },
});

export type DropdownMenuSubContentProps = ComponentProps<typeof DropdownMenuPrimitive.SubContent>;

export const DropdownMenuSubContentSlot = Slot<DropdownMenuSubContentProps>({
  displayName: 'DropdownMenu.SubContent',
  renderDefault: ({ children, className, ...props }) => {
    if (!children) {
      return null;
    }

    return (
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.SubContent
          data-spellbook-name="dropdown-menu-sub-content"
          className={cn(
            ...popperContentSideOffsetClassNames,
            'bg-surface-overlay-0',
            ...popperContentMotionClassNames,
            /**
             * @TODO [SPE-74] Semantic z-indices
             * https://linear.app/magicschool/issue/SPE-74/semantic-z-indices
             */
            'z-3000',
            'min-w-48',
            'max-w-60',
            'sm:max-w-100',
            'max-h-(--radix-dropdown-menu-content-available-height)',
            'origin-(--radix-dropdown-menu-content-transform-origin)',
            'overflow-x-hidden',
            'border-1',
            'border-border-default',
            'overflow-y-auto',
            'rounded-card-md',
            'shadow-popover',
            'focus-visible:outline-none',
            className,
          )}
          {...props}
        >
          {children}
        </DropdownMenuPrimitive.SubContent>
      </DropdownMenuPrimitive.Portal>
    );
  },
});

export const DropdownMenuRadioGroupSlot = Slot<ComponentProps<typeof DropdownMenuPrimitive.RadioGroup> & { id?: string }>({
  displayName: 'DropdownMenu.RadioGroup',
  renderDefault: ({ children, defaultValue, value: providedValue, onValueChange, id: providedId, ...props }) => {
    const { getRadioGroupValue, setRadioGroupValue } = useDropdownMenu();
    const id = useId(providedId);

    const isControlled = providedValue !== undefined;

    const contextValue = getRadioGroupValue(id, defaultValue as string);
    const value = isControlled ? String(providedValue) : contextValue;

    const handleValueChange = (newValue: string) => {
      if (!isControlled) {
        setRadioGroupValue(id, newValue);
      }
      onValueChange?.(newValue);
    };

    return (
      <DropdownMenuPrimitive.RadioGroup
        data-spellbook-name="dropdown-menu-radio-group"
        data-group-id={id}
        id={id}
        {...props}
        value={value}
        onValueChange={handleValueChange}
      >
        {children}
      </DropdownMenuPrimitive.RadioGroup>
    );
  },
});

export type DropdownMenuRadioItemProps = ComponentProps<typeof DropdownMenuPrimitive.RadioItem> & {
  value: string;
  label?: ReactNode;
  description?: ReactNode;
};

export const DropdownMenuRadioItemSlot = Slot<DropdownMenuRadioItemProps>({
  displayName: 'DropdownMenu.RadioItem',
  renderDefault: ({ value, label, description, className, disabled, children, ...props }) => {
    const { slots, rest } = useSlots(children, {
      leadingIcon: { Component: DropdownMenuItemLeadingIconSlot, props },
      trailingIcon: { Component: DropdownMenuItemTrailingIconSlot, props },
      label: {
        Component: DropdownMenuItemLabelSlot,
        props: {
          label,
        },
      },
      description: {
        Component: DropdownMenuItemDescriptionSlot,
        props: {
          label: description,
        },
      },
    });

    /**
     * @TODO [SPE-37] Utility for post-slot prop updates
     * https://linear.app/magicschool/issue/SPE-37/utility-for-post-slot-prop-updates
     */
    if (rest) {
      const labelSlot = slots.label;
      let labelSlotProps;
      if (Array.isArray(labelSlot) && labelSlot[0]) {
        labelSlotProps = {
          ...(labelSlot[0].props ?? {}),
          label: (
            <>
              {labelSlot[0].props.label}
              {rest}
            </>
          ),
        };
        slots.label = [
          {
            ...labelSlot[0],
            type: labelSlot[0].type,
            props: labelSlotProps,
          },
          ...labelSlot.slice(1),
        ];
      } else if (labelSlot && !Array.isArray(labelSlot)) {
        labelSlotProps = {
          ...(labelSlot.props ?? {}),
          label: (
            <>
              {labelSlot.props.label}
              {rest}
            </>
          ),
        };
        slots.label = {
          ...labelSlot,
          props: labelSlotProps,
        };
      }
    }

    return (
      <DropdownMenuPrimitive.RadioItem
        data-spellbook-name="dropdown-menu-radio-item"
        value={value}
        disabled={disabled}
        className={cn(
          [
            'flex',
            'cursor-pointer',
            'items-center',
            'justify-between',
            'outline-none',
            'px-3',
            'py-2',
            'gap-2',
            /**
             * @TODO [SPE-16] Centralize “Selected” Color Tokens into Shared Style Utilities
             * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
             */
            'data-[state=checked]:bg-selected-default-background',
            'data-[state=checked]:hover:bg-selected-default-background-hover',
            'data-[state=checked]:active:bg-selected-default-background-active',
            'data-[state=checked]:focus-visible:bg-selected-default-background-hover',
            'data-[state=checked]:text-selected-default-foreground',
            'data-[state=checked]:hover:text-selected-default-foreground-hover',
            'data-[state=checked]:focus-visible:text-selected-default-foreground-hover',
            'data-[state=checked]:active:text-selected-default-foreground-active',
            'data-[highlighted]:bg-surface-overlay-1',
            'data-[highlighted]:data-[state=checked]:bg-selected-default-background-hover',
            'hover:bg-surface-overlay-1',
            'focus:bg-surface-overlay-1',
            'text-default-text-strong',
            'data-[state=checked]:text-primary-text',
            'first:mt-2',
            'last:mb-2',
            'group',
            'aria-disabled:hover:bg-surface-overlay-0',
            'aria-disabled:text-disabled-foreground',
            'aria-disabled:cursor-not-allowed',
            'aria-disabled:data-[state=checked]:bg-selected-default-background',
          ],
          className,
        )}
        aria-disabled={disabled}
        {...props}
      >
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            {slots.leadingIcon}
            <span className="flex grow-1 me-4 flex-col">
              {slots.label}
              {slots.description}
            </span>
          </span>
        </span>
        <DropdownMenuPrimitive.ItemIndicator
          className={cn(
            'text-sm',
            /**
             * @TODO [SPE-16] Centralize “Selected” Color Tokens into Shared Style Utilities
             * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
             */
            'group-data-[state=checked]:text-selected-default-icon',
            'group-data-[state=checked]:group-hover:text-selected-default-icon-hover',
            'group-data-[state=checked]:group-focus-visible:text-selected-default-icon-hover',
            'group-data-[state=checked]:group-active:text-selected-default-icon-active',
          )}
        >
          <Icon name="check" color="inherit" size="sm" />
        </DropdownMenuPrimitive.ItemIndicator>
        {slots.trailingIcon && <span className="flex-0">{slots.trailingIcon}</span>}
      </DropdownMenuPrimitive.RadioItem>
    );
  },
});

export type DropdownMenuCheckboxItemProps = ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem> & {
  label?: ReactNode;
  description?: ReactNode;
};

export const DropdownMenuCheckboxItemSlot = Slot<DropdownMenuCheckboxItemProps>({
  displayName: 'DropdownMenu.CheckboxItem',
  renderDefault: ({ label, description, className, disabled, children, checked, onCheckedChange, ...props }) => {
    const { slots, rest } = useSlots(children, {
      leadingIcon: { Component: DropdownMenuItemLeadingIconSlot, props },
      trailingIcon: { Component: DropdownMenuItemTrailingIconSlot, props },
      label: {
        Component: DropdownMenuItemLabelSlot,
        props: {
          label,
        },
      },
      description: {
        Component: DropdownMenuItemDescriptionSlot,
        props: {
          label: description,
        },
      },
    });

    /**
     * @TODO [SPE-37] Utility for post-slot prop updates
     * https://linear.app/magicschool/issue/SPE-37/utility-for-post-slot-prop-updates
     */
    if (rest) {
      const labelSlot = slots.label;
      let labelSlotProps;
      if (Array.isArray(labelSlot) && labelSlot[0]) {
        labelSlotProps = {
          ...(labelSlot[0].props ?? {}),
          label: (
            <>
              {labelSlot[0].props.label}
              {rest}
            </>
          ),
        };
        slots.label = [
          {
            ...labelSlot[0],
            type: labelSlot[0].type,
            props: labelSlotProps,
          },
          ...labelSlot.slice(1),
        ];
      } else if (labelSlot && !Array.isArray(labelSlot)) {
        labelSlotProps = {
          ...(labelSlot.props ?? {}),
          label: (
            <>
              {labelSlot.props.label}
              {rest}
            </>
          ),
        };
        slots.label = {
          ...labelSlot,
          props: labelSlotProps,
        };
      }
    }

    return (
      <DropdownMenuPrimitive.CheckboxItem
        data-spellbook-name="dropdown-menu-checkbox-item"
        disabled={disabled}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className={cn(
          [
            'flex',
            'cursor-pointer',
            'items-center',
            'justify-between',
            'outline-none',
            'px-3',
            'py-2',
            'gap-2',
            /**
             * @TODO [SPE-16] Centralize “Selected” Color Tokens into Shared Style Utilities
             * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
             */
            'data-[state=checked]:bg-selected-default-background',
            'data-[state=checked]:hover:bg-selected-default-background-hover',
            'data-[state=checked]:active:bg-selected-default-background-active',
            'data-[state=checked]:focus-visible:bg-selected-default-background-hover',
            'data-[state=checked]:text-selected-default-foreground',
            'data-[state=checked]:hover:text-selected-default-foreground-hover',
            'data-[state=checked]:focus-visible:text-selected-default-foreground-hover',
            'data-[state=checked]:active:text-selected-default-foreground-active',
            'data-[highlighted]:bg-surface-overlay-1',
            'data-[highlighted]:data-[state=checked]:bg-selected-default-background-hover',
            'hover:bg-surface-overlay-1',
            'focus-visible:bg-surface-overlay-1',
            'text-default-text-strong',
            'data-[state=checked]:text-primary-text',
            'first:mt-2',
            'last:mb-2',
            'group',
            'aria-disabled:hover:bg-surface-overlay-0',
            'aria-disabled:text-disabled-foreground',
            'aria-disabled:cursor-not-allowed',
            'aria-disabled:data-[state=checked]:bg-selected-default-background',
          ],
          className,
        )}
        aria-disabled={disabled}
        {...props}
      >
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            {slots.leadingIcon}
            <span className="flex grow-1 me-4 flex-col">
              {slots.label}
              {slots.description}
            </span>
          </span>
        </span>
        <DropdownMenuPrimitive.ItemIndicator
          className={cn(
            'text-sm',
            'group-data-[state=checked]:text-selected-default-icon',
            'group-data-[state=checked]:group-hover:text-selected-default-icon-hover',
            'group-data-[state=checked]:group-focus-visible:text-selected-default-icon-hover',
            'group-data-[state=checked]:group-active:text-selected-default-icon-active',
          )}
        >
          <Icon name="check" color="inherit" size="sm" />
        </DropdownMenuPrimitive.ItemIndicator>
        {slots.trailingIcon && <span className="flex-0">{slots.trailingIcon}</span>}
      </DropdownMenuPrimitive.CheckboxItem>
    );
  },
});

export interface DropdownMenuModalItemProps extends DropdownMenuItemProps {
  /**
   * When true, the dropdown remains open and focus returns to the item
   * that triggered the modal after the modal closes. When false (default),
   * the dropdown closes and focus returns to the dropdown trigger button.
   * @default false
   */
  preserveDropdownOnModalClose?: boolean;
  /**
   * Whether the modal is currently open. When provided, the modal operates in controlled mode
   * where its state is managed externally.
   */
  modalOpen?: boolean;
  /**
   * Callback when the modal open state changes. Only used in internal (uncontrolled) mode.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Callback when the modal closes and is about to restore focus. Only used in internal mode.
   */
  onCloseAutoFocus?: (event: Event) => void;
  /**
   * Optional key to coordinate focus restoration with Modal's returnFocusKey prop.
   * When provided, uses keyed focus coordination. When omitted, uses automatic
   * focus tracking (recommended for most cases). Only needed when multiple modals
   * share the same trigger or for complex focus scenarios.
   */
  returnFocusKey?: string;
  /** Size of the internally managed modal. Ignored in controlled mode. */
  modalSize?: ModalProps['size'];
}

const DropdownMenuModalItemComponent = forwardRef<HTMLDivElement, DropdownMenuModalItemProps>(
  (
    {
      onSelect,
      children,
      preserveDropdownOnModalClose = false,
      modalOpen,
      onOpenChange,
      onCloseAutoFocus,
      returnFocusKey,
      modalSize,
      'data-spellbook-name': spellbookName,
    },
    forwardedRef,
  ) => {
    const { closeDropdownMenu, openModal, closeModal, hasOpenModal, triggerRef } = useDropdownMenu();
    const wasModalOpen = useRef(modalOpen);
    const internalRef = useRef<HTMLDivElement>(null);
    const itemRef = (forwardedRef as RefObject<HTMLDivElement>) || internalRef;

    // Controlled mode: User provides modalOpen, managed by external Modal.
    // Uncontrolled mode: Component manages internal Modal.
    const isControlled = modalOpen !== undefined;

    const handleSelect = (event: Event) => {
      event.preventDefault();
      onSelect?.(event);
    };

    const { slots } = useSlots(children, {
      trigger: {
        Component: DropdownMenuItemSlot,
        props: { ref: itemRef, onSelect: handleSelect, 'data-spellbook-name': spellbookName },
      },
      actionRow: { Component: DropdownMenuActionRowSlot },
      modalContent: { Component: ModalContentSlot },
      modalHeader: { Component: ModalHeaderSlot },
      modalDescription: { Component: ModalDescriptionSlot },
      modalFooter: { Component: ModalFooterSlot },
    });

    // Track controlled modal state changes to optionally close dropdown
    useEffect(() => {
      if (isControlled && wasModalOpen.current && !modalOpen && !preserveDropdownOnModalClose) {
        closeDropdownMenu();
      }
      wasModalOpen.current = modalOpen;
    }, [isControlled, modalOpen, closeDropdownMenu, preserveDropdownOnModalClose]);

    const handleModalItemOpenChange = (open: boolean) => {
      if (open) {
        openModal();
      } else {
        closeModal();
        if (!preserveDropdownOnModalClose) {
          closeDropdownMenu();
        }
      }

      onOpenChange?.(open);
    };

    const handleModalCloseAutoFocus = (event: Event) => {
      if (onCloseAutoFocus) {
        onCloseAutoFocus(event);
        return;
      }
      if (preserveDropdownOnModalClose) {
        event.preventDefault();
        itemRef.current?.focus();
      }
    };

    const renderTrigger = (triggerProps: Pick<DropdownMenuItemProps, 'onSelect' | 'aria-haspopup' | 'aria-expanded'>) =>
      isValidElement<DropdownMenuActionRowProps>(slots.actionRow) ? (
        cloneElement(slots.actionRow, { ref: itemRef, 'data-spellbook-name': spellbookName, ...triggerProps } as DropdownMenuActionRowProps)
      ) : (
        <DropdownMenuItemSlot data-spellbook-name={spellbookName} ref={itemRef} {...triggerProps}>
          {children}
        </DropdownMenuItemSlot>
      );

    // Controlled modal mode
    if (isControlled) {
      if (preserveDropdownOnModalClose) {
        const handleSelect = (event: Event) => {
          event.preventDefault(); // Keep dropdown open
          onSelect?.(event);
        };

        // Wrap in ControlledModalTrigger to restore focus to item when modal closes
        return (
          <ControlledModalTrigger modalOpen={modalOpen} returnFocusKey={returnFocusKey}>
            {renderTrigger({ onSelect: handleSelect })}
          </ControlledModalTrigger>
        );
      }

      const handleSelect = (event: Event) => {
        // Register the dropdown trigger for focus restoration when modal closes
        if (triggerRef?.current && returnFocusKey) {
          // Use keyed coordination when key is provided
          specifyModalReturnFocus(returnFocusKey, triggerRef);
        } else if (triggerRef?.current) {
          // Fall back to automatic tracking (same as ControlledModalTrigger)
          trackLastClickedTrigger(triggerRef);
        }

        // Close the dropdown menu since preserveDropdownOnModalClose is false
        closeDropdownMenu();

        onSelect?.(event);
      };

      return renderTrigger({ onSelect: handleSelect, 'aria-haspopup': 'dialog', 'aria-expanded': modalOpen ?? false });
    }

    // Internal (uncontrolled) modal mode
    return (
      <Modal size={modalSize} open={hasOpenModal} onOpenChange={handleModalItemOpenChange} onCloseAutoFocus={handleModalCloseAutoFocus}>
        <Modal.Trigger asChild>{isValidElement(slots.actionRow) ? renderTrigger({ onSelect: handleSelect }) : slots.trigger}</Modal.Trigger>
        {slots.modalHeader}
        {slots.modalDescription}
        {slots.modalContent}
        {slots.modalFooter}
      </Modal>
    );
  },
);

const DropdownMenuModalItemBase = Slot<DropdownMenuModalItemProps>({
  displayName: 'DropdownMenu.ModalItem',
  preventOverride: true,
  renderDefault: (props) => <DropdownMenuModalItemComponent data-spellbook-name="dropdown-menu-modal-item" {...props} />,
});

export const DropdownMenuItemSlot = Object.assign(DropdownMenuItemBase, {
  LeadingIcon: DropdownMenuItemLeadingIconSlot,
  TrailingIcon: DropdownMenuItemTrailingIconSlot,
  Label: DropdownMenuItemLabelSlot,
  Description: DropdownMenuItemDescriptionSlot,
  Avatar: DropdownMenuItemAvatarSlot,
  ContentOnly: DropdownMenuItemContentOnlySlot,
});

export const DropdownMenuModalItemSlot = Object.assign(DropdownMenuModalItemBase, {
  Trigger: DropdownMenuItemSlot,
  ActionRow: DropdownMenuActionRowSlot,
  ModalHeader: ModalHeaderSlot,
  ModalDescription: ModalDescriptionSlot,
  ModalContent: ModalContentSlot,
  ModalFooter: ModalFooterSlot,
  ModalClose: ModalCloseSlot,
});
